import type { PostgrestError } from '@supabase/supabase-js'

interface PageResult<T> {
	data: T[] | null
	error: PostgrestError | null
}

const DEFAULT_PAGE_SIZE = 1000

interface FetchAllPaginatedOptions {
	pageSize?: number
	maxRows?: number
}

/**
 * Fetch every row of a Supabase query by paging with `.range()`, avoiding
 * PostgREST's silent default 1000-row cap (and any hard `.limit()` that would
 * truncate results without warning).
 *
 * Pass a `buildPage` factory that applies `.range(from, to)` to a fresh query
 * for each page. Returns the accumulated rows and the first error encountered
 * (rows collected before the error are still returned). Stops when a page comes
 * back short (fewer than `pageSize` rows), on error, or once `maxRows` is hit.
 */
export async function fetchAllPaginated<T>(
	buildPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
	options: FetchAllPaginatedOptions = {},
): Promise<{ data: T[]; error: PostgrestError | null }> {
	const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE
	const maxRows = options.maxRows ?? Number.POSITIVE_INFINITY
	const all: T[] = []

	for (let from = 0; from < maxRows; from += pageSize) {
		const to = Math.min(from + pageSize, maxRows) - 1
		const { data, error } = await buildPage(from, to)
		if (error) return { data: all, error }
		if (!data || data.length === 0) break
		all.push(...data)
		if (data.length < pageSize || all.length >= maxRows) break
	}

	return { data: all, error: null }
}
